/**
 * Regional Settings Page - Currency, timezone, and localization settings
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import LanguageIcon from '@mui/icons-material/Language';
import Layout from '../../components/layout/Layout';
import PageTitle from '../../components/PageTitle';
import { useSnackbar } from 'notistack';
import companySettingsService from '../../services/companySettingsService';
import { CURRENCY_CONFIGS, setCurrencySettings, formatCurrency } from '../../utils/currencyFormatter';
import { setDateFormatSettings } from '../../utils/dateFormatter';

const PAGE_BG = '#f4f6f8';
const PANEL_BORDER = '1px solid #e5e7eb';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`regional-settings-tabpanel-${index}`}>
      {value === index ? children : null}
    </div>
  );
}

const RegionalSettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState({
    currencyCode: 'INR',
    currencySymbol: '₹',
    timezone: 'UTC',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: '12',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-US',
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const TIMEZONES = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  ];

  const DATE_FORMATS = [
    { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy (12/31/2024)' },
    { value: 'dd/MM/yyyy', label: 'dd/MM/yyyy (31/12/2024)' },
    { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd (2024-12-31)' },
    { value: 'dd-MM-yyyy', label: 'dd-MM-yyyy (31-12-2024)' },
    { value: 'MMM dd, yyyy', label: 'MMM dd, yyyy (Dec 31, 2024)' },
    { value: 'dd MMM yyyy', label: 'dd MMM yyyy (31 Dec 2024)' },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const changed = Object.keys(settings).some((key) => settings[key] !== originalSettings[key]);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await companySettingsService.getSettings();

      const regionalSettings = {
        currencyCode: data.currencyCode || 'INR',
        currencySymbol: data.currencySymbol || '₹',
        timezone: data.timezone || 'UTC',
        dateFormat: data.dateFormat || 'MM/dd/yyyy',
        timeFormat: data.timeFormat || '12',
        decimalPlaces: data.decimalPlaces || 2,
        thousandSeparator: data.thousandSeparator || ',',
        decimalSeparator: data.decimalSeparator || '.',
        languageCode: data.languageCode || 'en-US',
      };

      setSettings(regionalSettings);
      setOriginalSettings(regionalSettings);
      setCurrencySettings(regionalSettings);
      setDateFormatSettings({
        dateFormat: regionalSettings.dateFormat,
        timeFormat: regionalSettings.timeFormat,
        timezone: regionalSettings.timezone,
      });
    } catch {
      enqueueSnackbar('Failed to load regional settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCurrencyPresetChange = (currencyCode) => {
    const config = CURRENCY_CONFIGS[currencyCode];
    if (config) {
      setSettings((prev) => ({
        ...prev,
        ...config,
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await companySettingsService.updateSettings(settings);

      if (response.success) {
        setOriginalSettings(settings);
        setCurrencySettings(settings);
        setDateFormatSettings({
          dateFormat: settings.dateFormat,
          timeFormat: settings.timeFormat,
          timezone: settings.timezone,
        });
        enqueueSnackbar(response.message, { variant: 'success' });

        if (response.note) {
          enqueueSnackbar(response.note, {
            variant: 'info',
            autoHideDuration: 8000,
          });
        }
      } else {
        enqueueSnackbar(response.message || 'Failed to update settings', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Failed to save regional settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
  };

  const actionBar = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      {hasChanges && (
        <Chip size="small" label="Unsaved" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
      )}
      <Button variant="outlined" size="small" onClick={handleReset} disabled={!hasChanges || saving}>
        Reset
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={!hasChanges || saving}
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </Stack>
  );

  if (loading) {
    return (
      <Layout>
        <PageTitle title="Regional Settings" />
        <Box sx={{ bgcolor: PAGE_BG, minHeight: '70vh' }}>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
              <CircularProgress />
            </Box>
          </Container>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageTitle title="Regional Settings" />
      <Box sx={{ bgcolor: PAGE_BG, pb: 4 }}>
        <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: PANEL_BORDER,
              borderRadius: 2.5,
              overflow: 'hidden',
              bgcolor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                borderBottom: PANEL_BORDER,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <LanguageIcon sx={{ fontSize: 22, color: '#fff' }} />
              </Box>
              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 }}>
                  Regional settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Currency, date, time, and language preferences for your business.
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>{actionBar}</Box>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Regional settings sections"
              sx={{
                px: { xs: 1, sm: 2 },
                borderBottom: PANEL_BORDER,
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  py: 1.25,
                  px: 2,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&.Mui-selected': { color: 'primary.main', fontWeight: 600 },
                },
                '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
              }}
            >
              <Tab label="Currency" id="regional-settings-tab-0" aria-controls="regional-settings-tabpanel-0" />
              <Tab label="Date & time" id="regional-settings-tab-1" aria-controls="regional-settings-tabpanel-1" />
            </Tabs>

            <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
              <TabPanel value={activeTab} index={0}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Quick currency selection
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {Object.entries(CURRENCY_CONFIGS).map(([code, config]) => (
                        <Chip
                          key={code}
                          size="small"
                          label={`${config.currencySymbol} ${code}`}
                          onClick={() => handleCurrencyPresetChange(code)}
                          color={settings.currencyCode === code ? 'primary' : 'default'}
                          variant={settings.currencyCode === code ? 'filled' : 'outlined'}
                          clickable
                          sx={{ fontWeight: settings.currencyCode === code ? 600 : 500 }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Currency code"
                        value={settings.currencyCode}
                        onChange={handleInputChange('currencyCode')}
                        inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Currency symbol"
                        value={settings.currencySymbol}
                        onChange={handleInputChange('currencySymbol')}
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Decimal places</InputLabel>
                        <Select
                          value={settings.decimalPlaces}
                          onChange={handleInputChange('decimalPlaces')}
                          label="Decimal places"
                        >
                          <MenuItem value={0}>0 (¥1,234)</MenuItem>
                          <MenuItem value={2}>2 ($1,234.56)</MenuItem>
                          <MenuItem value={3}>3 ($1,234.567)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Thousand separator</InputLabel>
                        <Select
                          value={settings.thousandSeparator}
                          onChange={handleInputChange('thousandSeparator')}
                          label="Thousand separator"
                        >
                          <MenuItem value=",">, (Comma) - 1,234.56</MenuItem>
                          <MenuItem value=".">. (Period) - 1.234,56</MenuItem>
                          <MenuItem value=" "> (Space) - 1 234.56</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Decimal separator</InputLabel>
                        <Select
                          value={settings.decimalSeparator}
                          onChange={handleInputChange('decimalSeparator')}
                          label="Decimal separator"
                        >
                          <MenuItem value=".">. (Period) - 1,234.56</MenuItem>
                          <MenuItem value=",">, (Comma) - 1.234,56</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: PANEL_BORDER,
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Preview
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {formatCurrency(1234.56, settings)} • {formatCurrency(1000000, settings)}
                    </Typography>
                  </Box>
                </Stack>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={settings.timezone}
                        onChange={handleInputChange('timezone')}
                        label="Timezone"
                      >
                        {TIMEZONES.map((tz) => (
                          <MenuItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Date format</InputLabel>
                      <Select
                        value={settings.dateFormat}
                        onChange={handleInputChange('dateFormat')}
                        label="Date format"
                      >
                        {DATE_FORMATS.map((format) => (
                          <MenuItem key={format.value} value={format.value}>
                            {format.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Time format</InputLabel>
                      <Select
                        value={settings.timeFormat}
                        onChange={handleInputChange('timeFormat')}
                        label="Time format"
                      >
                        <MenuItem value="12">12-hour (2:30 PM)</MenuItem>
                        <MenuItem value="24">24-hour (14:30)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Language code"
                      value={settings.languageCode}
                      onChange={handleInputChange('languageCode')}
                      inputProps={{ maxLength: 5 }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Layout>
  );
};

export default RegionalSettings;
